import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    ButtonBase,
    CardContent,
    ClickAwayListener,
    Grid,
    Paper,
    Popper,
} from '@mui/material';

// project import
import { AuthState, MenuState, Store } from 'store/types';
import { CustomTheme } from 'themes/Theme';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import SettingTab from './SettingTab';
import UserItem from '../../../../../components/user/UserItem';

import { clearAuth } from '../../../../../store/reducers/auth';
import {
    clearExtensions,
    openUserMenu,
} from '../../../../../store/reducers/menu';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile = () => {
    const theme = useTheme<CustomTheme>();
    const dispatch = useDispatch();

    const { userMenuOpen } = useSelector<Store, MenuState>(state => state.menu);
    const { user } = useSelector<Store, AuthState>(state => state.auth);

    const handleLogout = () => {
        dispatch(clearExtensions());
        dispatch(clearAuth());
    };

    const anchorRef = useRef<HTMLButtonElement | null>(null);

    const handleToggle = () => {
        dispatch(openUserMenu({ userMenuOpen: !userMenuOpen }));
    };

    const handleClose = (event: React.MouseEvent | MouseEvent | TouchEvent) => {
        const checkEventTarget = (target: EventTarget | null): target is Node =>
            !!target && target instanceof Node;
        if (
            anchorRef.current &&
            checkEventTarget(event.target) &&
            anchorRef.current.contains(event.target)
        ) {
            return;
        }
        dispatch(openUserMenu({ userMenuOpen: false }));
    };

    useEffect(() => {
        return () => {
            dispatch(openUserMenu({ userMenuOpen: false }));
        };
    }, []);

    const iconBackColorOpen = 'grey.300';

    return (
        <Box sx={{ flexShrink: 0, ml: 0.75 }}>
            <ButtonBase
                sx={{
                    p: 0.25,
                    bgcolor: userMenuOpen ? iconBackColorOpen : 'transparent',
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'secondary.lighter' },
                }}
                aria-label="open profile"
                ref={anchorRef}
                aria-controls={userMenuOpen ? 'profile-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}>
                <UserItem
                    user={user}
                    trackUserActivity
                    spacing
                    showBadge
                    showRipple
                    forceFullName
                />
            </ButtonBase>
            <Popper
                placement="bottom-end"
                open={userMenuOpen}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 9],
                            },
                        },
                    ],
                }}>
                {({ TransitionProps }) => (
                    <Transitions
                        type="fade"
                        in={userMenuOpen}
                        {...TransitionProps}>
                        {userMenuOpen && (
                            <Paper
                                sx={{
                                    boxShadow: theme.customShadows.z1,
                                    width: 290,
                                    minWidth: 240,
                                    maxWidth: 290,
                                    [theme.breakpoints.down('md')]: {
                                        maxWidth: 250,
                                    },
                                }}>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MainCard
                                        elevation={0}
                                        border={false}
                                        content={false}>
                                        <CardContent sx={{ px: 2, pt: 2 }}>
                                            <Grid
                                                container
                                                justifyContent="space-between"
                                                alignItems="center">
                                                <Grid item>
                                                    <UserItem
                                                        user={user}
                                                        showEmail
                                                        forceFullName
                                                    />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                        <SettingTab
                                            handleLogout={handleLogout}
                                            handleClose={handleClose}
                                        />
                                    </MainCard>
                                </ClickAwayListener>
                            </Paper>
                        )}
                    </Transitions>
                )}
            </Popper>
        </Box>
    );
};

export default Profile;
